<script setup lang="ts">
  import { Button, Typo, Tag, type TypoVariant } from '@travelata-libs/ui-kit';
  import { formatPhone } from '@travelata-libs/utils';
  import { ref, computed, nextTick } from 'vue';
  import { LeadBannerAppearance, type LeadBannerPayload } from './LeadBanner.types';
  import LeadBannerAvatars from './LeadBannerAvatars.vue';
  import LeadBannerForm from './LeadBannerForm.vue';
  import LeadBannerModal from './LeadBannerModal.vue';
  import LeadBannerSuccess from './LeadBannerSuccess.vue';
  import { TAG_RESPONSE_TIME, TAG_EXPERIENCE } from './lib';

  export interface LeadBannerProps {
    title: string;
    subtitle?: string;
    subtitleVariant?: `${TypoVariant}`;
    appearance?: `${LeadBannerAppearance}`;
    mobile?: boolean;
    modelValue: LeadBannerPayload;
    isSubmitted?: boolean;
    loading?: boolean;
    userAgreementUrl: string;
    privacyPolicyUrl: string;
    zIndex?: number;
    dataProcessingUrl: string;
    buttonText: string;
    modalTitle?: string;
  }

  interface LeadBannerEmits {
    (e: 'submit', payload: LeadBannerPayload): void;
    (e: 'update:modelValue', value: LeadBannerPayload): void;
    (e: 'focus'): void;
    (e: 'sheetOpen'): void;
  }

  const emit = defineEmits<LeadBannerEmits>();

  const props = withDefaults(defineProps<LeadBannerProps>(), {
    subtitle: undefined,
    subtitleVariant: 'body-l-400',
    mobile: false,
    zIndex: 5,
    isSubmitted: false,
    loading: false,
    appearance: LeadBannerAppearance.Default,
    modalTitle: 'Подберём тур вместе',
  });

  const isModalOpen = ref(false);
  const formRef = ref<InstanceType<typeof LeadBannerModal> | null>(null);
  const desktopFormRef = ref<InstanceType<typeof LeadBannerForm> | null>(null);

  const maskedPhone = computed(() => formatPhone(props.modelValue.phone));
  const successSubtitleText = computed(() => {
    if (!maskedPhone.value) return 'Перезвоним в рабочее время — с 7:00 до 21:00 по МСК, ежедневно';

    return `Перезвоним на ${maskedPhone.value} в рабочее время — с 7:00 до 21:00 по МСК, ежедневно`;
  });

  const modalProps = computed(() => ({
    open: isModalOpen.value,
    mobile: props.mobile,
    zIndex: props.zIndex,
    loading: props.loading,
    buttonText: 'Отправить заявку',
    userAgreementUrl: props.userAgreementUrl,
    privacyPolicyUrl: props.privacyPolicyUrl,
    dataProcessingUrl: props.dataProcessingUrl,
  }));

  const openModal = async () => {
    isModalOpen.value = true;
    emit('sheetOpen');
    await nextTick();
    formRef.value?.focusName();
  };

  const closeModal = () => {
    isModalOpen.value = false;
  };

  const handleSubmit = (payload: LeadBannerPayload) => {
    if (props.isSubmitted || props.loading) return;
    emit('submit', payload);
  };

  const formModel = computed({
    get: () => props.modelValue,
    set: (value) => {
      emit('update:modelValue', value);
    },
  });

  defineExpose({
    closeModal,
    blur: () => {
      desktopFormRef.value?.blur();
      formRef.value?.blur();
    },
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    :class="['tl-lead-banner', `tl-lead-banner_${appearance}`, { 'tl-lead-banner_mobile': mobile }]"
    data-test-id="lead-banner"
  >
    <template v-if="!mobile">
      <LeadBannerSuccess v-if="isSubmitted" :subtitle="successSubtitleText" />

      <div v-else class="tl-lead-banner__content" data-test-id="lead-banner-content">
        <template v-if="appearance === 'default'">
          <div class="tl-lead-banner__heading-wrapper">
            <div class="tl-lead-banner__heading">
              <Typo variant="h-3" class="tl-lead-banner__title">{{ title }}</Typo>
              <Typo v-if="subtitle" :variant="subtitleVariant" class="tl-lead-banner__subtitle">
                {{ subtitle }}
              </Typo>
            </div>

            <div class="tl-lead-banner__social">
              <LeadBannerAvatars :mobile="mobile" />

              <div class="tl-lead-banner__tags">
                <Tag color="green" appearance="primary">{{ TAG_RESPONSE_TIME }}</Tag>
                <Tag color="green" appearance="primary">{{ TAG_EXPERIENCE }}</Tag>
              </div>
            </div>
          </div>

          <LeadBannerForm
            ref="desktopFormRef"
            v-model="formModel"
            class="tl-lead-banner__form"
            :loading="loading"
            :mobile="mobile"
            :user-agreement-url="userAgreementUrl"
            :privacy-policy-url="privacyPolicyUrl"
            :button-text="buttonText"
            :data-processing-url="dataProcessingUrl"
            @submit="handleSubmit"
            @focus="emit('focus')"
          />
        </template>

        <template v-else>
          <LeadBannerAvatars :count="3" size="l" centered :mobile="false" />

          <div class="tl-lead-banner__heading">
            <Typo variant="h-5" class="tl-lead-banner__title">{{ title }}</Typo>
            <Typo variant="body-l-400" class="tl-lead-banner__subtitle">
              {{ subtitle }}
            </Typo>
          </div>

          <Button class="tl-lead-banner__button" appearance="secondary" size="l" @click="openModal">
            Подобрать с экспертом
          </Button>

          <LeadBannerModal
            ref="formRef"
            v-model="formModel"
            v-bind="modalProps"
            closable
            centered-button
            :overlay-closable="false"
            @submit="handleSubmit"
            @focus="emit('focus')"
            @close="isModalOpen = false"
          >
            <Typo class="tl-lead-banner__modal-header" variant="h-5">{{ modalTitle }}</Typo>
            <Typo class="tl-lead-banner__modal-header-subtitle" variant="body-l-400">
              Оставьте контакты — эксперт перезвонит и поможет с выбором
            </Typo>
          </LeadBannerModal>
        </template>
      </div>
    </template>

    <template v-else>
      <div v-if="!isSubmitted" class="tl-lead-banner__content" data-test-id="lead-banner-content-mobile">
        <Typo variant="body-l-500" class="tl-lead-banner__title">{{ title }}</Typo>

        <LeadBannerAvatars mobile />

        <Button data-test-id="lead-banner-mobile-btn" @click="openModal">{{ buttonText }}</Button>

        <LeadBannerModal
          ref="formRef"
          v-model="formModel"
          v-bind="modalProps"
          @submit="handleSubmit"
          @focus="emit('focus')"
          @close="isModalOpen = false"
        >
          <Typo class="tl-lead-banner__modal-header tl-lead-banner__modal-header_mobile" variant="h-5">
            Контакты для связи
          </Typo>
        </LeadBannerModal>
      </div>

      <div v-else class="tl-lead-banner__success" data-test-id="lead-banner-success-mobile">
        <Typo variant="body-l-500" class="tl-lead-banner__success-title">
          Заявка принята. Вам перезвонит специалист на номер {{ maskedPhone }}
        </Typo>

        <LeadBannerAvatars mobile centered />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
  .tl-lead-banner {
    $root: &;

    background-color: var(--color-bg-primary);
    border-radius: var(--rounding-s);
    box-shadow: var(--shadow-s);
    color: var(--color-text-primary);
    display: flex;
    justify-content: center;
    padding: var(--spacing-xxxl) var(--spacing-xxl);

    &_compact {
      padding: var(--spacing-l);
    }

    &_mobile {
      padding: var(--spacing-l) var(--spacing-m);
    }

    &__content {
      align-items: center;
      display: flex;
      gap: var(--spacing-xxxl);
      justify-content: space-between;
      max-width: 952px;
      width: 100%;

      #{$root}_compact & {
        gap: 0;
        justify-content: center;
      }

      #{$root}_mobile & {
        flex-direction: column;
        gap: var(--spacing-m);
        justify-content: normal;
        max-width: none;
        text-align: center;
        width: auto;
      }
    }

    &__heading-wrapper {
      display: flex;
      flex: 1 1 420px;
      flex-direction: column;
      height: 100%;
      justify-content: space-between;
      max-width: 420px;
      min-width: 342px;
    }

    &__heading {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);

      #{$root}_compact & {
        gap: var(--spacing-xxs);
        margin-left: var(--spacing-m);
        margin-right: var(--spacing-l);
        width: max-content;
      }
    }

    &__social {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-s);
    }

    &__tags {
      display: flex;
      gap: var(--spacing-xs);
    }

    &__form {
      height: 100%;
      max-width: 420px;
      min-width: 282px;
      width: 100%;
    }

    &__button {
      border-color: inherit;
      color: inherit;
      min-width: max-content;
      #{$root}_compact & {
        margin-left: auto;
      }
    }

    &__modal-header {
      color: var(--color-text-primary);
      margin-bottom: var(--spacing-l);

      &_mobile {
        margin-bottom: calc(var(--spacing-l) + var(--spacing-m));
        text-align: center;
      }
    }

    &__modal-header-subtitle {
      color: var(--color-text-primary);
      margin-bottom: var(--spacing-xl);
      max-width: 356px;
    }

    &__success {
      align-items: center;
      display: flex;
      flex-direction: column;
      text-align: center;
    }

    &__success-title {
      margin-bottom: var(--spacing-xs);
    }
  }
</style>
